/**
 * Path: packages/frontend/src/core/composables/useAppStyle.ts
 * Task: Provide a reusable, modular composable to dynamically resolve entity and status styles entirely from app configuration.
 * Refactoring: Enhanced to deeply read icon, color, and iconShape from all relevant app-data.json blocks (projectTypes, userTypes, statusCategories, valueTypes).
 */
import { useAuthStore } from '@/store/auth';

export interface AppStyleResult {
  name?: string;
  title?: string;
  color: string;
  colorSystem?: string;
  icon: string;
  iconSystem?: string | null;
  description?: string;
  iconShape?: string;
}

export function useAppStyle() {
  const authStore = useAuthStore();

  const colorPalette: Record<string, string> = {
    'deep-orange': '#FF5722',
    'cyan': '#00BCD4',
    'pink': '#E91E63',
    'light-green': '#8BC34A',
    'brown': '#795548',
    'amber': '#FFC107',
    'deep-purple': '#673AB7',
    'grey': '#9E9E9E',
    'blue-grey': '#607D8B',
    'primary': '#00c3cd',
    'secondary': '#5CBBF6',
    'work': '#C62828',
    'pilot': '#F9A825',
    'published': '#558B2F'
  };

  const resolveHexColor = (colorName: string): string => {
    if (!colorName) return '#E0E0E0';
    if (colorName.startsWith('#')) return colorName;

    // Check custom colors from app configuration
    const appColors = authStore.appData?.colors || {};
    if (appColors[colorName]) return appColors[colorName];
    if (appColors[colorName.toLowerCase()]) return appColors[colorName.toLowerCase()];

    // Fallback to default palette
    return colorPalette[colorName.toLowerCase()] || colorName;
  };

  const getStatusStyle = (statusKey: string): AppStyleResult => {
    if (!statusKey || statusKey === 'UNASSIGNED') {
      return { name: 'Unassigned', color: '#9E9E9E', icon: 'mdi-circle-outline', iconShape: 'circle' };
    }

    const appData = authStore.appData;

    // 1. Try to find it in the global status types list (from DB via app-data)
    const globalStatuses = appData?.statusTypes || [];
    const statusDef = globalStatuses.find((s: any) => s.id === statusKey || s.system_key === statusKey);

    if (statusDef) {
      // If the DB has a category defined, we try to pull the icon/shape from the static category config
      let categoryIcon = 'mdi-circle';
      let categoryShape = 'circle';

      if (statusDef.system && appData?.statusCategories?.[statusDef.system]) {
        categoryIcon = appData.statusCategories[statusDef.system].icon || categoryIcon;
        categoryShape = appData.statusCategories[statusDef.system].iconShape || categoryShape;
      }

      return {
        name: statusDef.name_en || statusDef.name_ger || statusKey,
        color: resolveHexColor(statusDef.color || 'blue-grey'),
        icon: statusDef.icon || categoryIcon,
        iconShape: statusDef.iconShape || categoryShape
      };
    }

    // 2. Try to find it directly in static statusCategories
    if (appData?.statusCategories?.[statusKey]) {
      const cat = appData.statusCategories[statusKey];
      return {
        name: cat.label || statusKey,
        color: resolveHexColor(cat.color || '#607D8B'),
        icon: cat.icon || 'mdi-circle',
        iconShape: cat.iconShape || 'circle'
      }
    }

    // Fallback
    return {
      name: statusKey.replace(/_/g, ' '),
      color: '#607D8B',
      icon: 'mdi-circle',
      iconShape: 'circle'
    };
  };

  const getEntityStyle = (entityType: string): AppStyleResult => {
    if (!entityType) return { title: 'Unknown', icon: 'mdi-help-circle-outline', color: '#9E9E9E', iconShape: 'squircle' };

    const normalizedType = entityType.toLowerCase();
    const appData = authStore.appData;

    // 1. Try standard DB Entities
    const entitiesDef = appData?.entities || [];
    const foundEntity = entitiesDef.find((e: any) =>
      e.name.toLowerCase() === normalizedType ||
      e.name.toLowerCase() === normalizedType + 's' ||
      e.name.toLowerCase() + 's' === normalizedType
    );

    if (foundEntity) {
      return {
        title: foundEntity.title || foundEntity.name,
        icon: foundEntity.symbol || foundEntity.icon || 'mdi-help-circle-outline',
        iconSystem: foundEntity.symbolSystem || null,
        color: resolveHexColor(foundEntity.color || 'grey'),
        colorSystem: resolveHexColor(foundEntity.colorSystem || '#000000'),
        description: foundEntity.description || '',
        iconShape: foundEntity.iconShape || 'squircle'
      };
    }

    // 2. Try User Types (Roles / Admins)
    const userTypes = appData?.userTypes || {};
    // Check systemRoles
    if (userTypes.systemRoles) {
      for (const [key, roleDef] of Object.entries(userTypes.systemRoles) as [string, any][]) {
        if (key.toLowerCase() === normalizedType) {
          return {
            title: roleDef.name || key,
            color: resolveHexColor(roleDef.color || '#607D8B'),
            icon: roleDef.icon || 'mdi-account',
            iconShape: roleDef.iconShape || 'circle'
          };
        }
      }
    }
    // Check projectRoles
    if (userTypes.projectRoles) {
      for (const [key, roleDef] of Object.entries(userTypes.projectRoles) as [string, any][]) {
        if (key.toLowerCase() === normalizedType) {
          return {
            title: roleDef.name || key,
            color: resolveHexColor(roleDef.color || '#607D8B'),
            icon: roleDef.icon || 'mdi-account',
            iconShape: roleDef.iconShape || 'circle'
          };
        }
      }
    }

    // 3. Try Value Types (Fields)
    const valueTypes = appData?.valueTypes || [];
    const foundValueType = valueTypes.find((v: any) => v.value.toLowerCase() === normalizedType);
    if (foundValueType) {
      return {
        title: foundValueType.title || foundValueType.value,
        color: resolveHexColor(foundValueType.color || '#9E9E9E'), // Default for fields without explicit color
        icon: foundValueType.icon || 'mdi-format-text',
        iconShape: foundValueType.iconShape || 'none'
      }
    }

    // 4. Try Project Types
    const projectTypes = appData?.projectTypes || {};
    if (projectTypes[normalizedType]) {
      return {
        title: projectTypes[normalizedType].name || normalizedType,
        color: resolveHexColor(projectTypes[normalizedType].color || '#424242'),
        icon: projectTypes[normalizedType].icon || 'mdi-folder',
        iconShape: projectTypes[normalizedType].iconShape || 'squircle'
      }
    }

    // Default Fallback
    return {
      title: entityType.charAt(0).toUpperCase() + entityType.slice(1),
      icon: 'mdi-help-circle-outline',
      color: '#9E9E9E',
      iconShape: 'squircle'
    };
  };

  const getProjectTypeStyle = (visibility: string): AppStyleResult => {
    const typeKey = visibility === 'template' ? 'template' : 'project';
    const typesConfig = authStore.appData?.projectTypes || {};

    if (typesConfig[typeKey]) {
      return {
        name: typesConfig[typeKey].name || typeKey,
        color: resolveHexColor(typesConfig[typeKey].color || '#424242'),
        icon: typesConfig[typeKey].icon || 'mdi-folder',
        iconShape: typesConfig[typeKey].iconShape || 'squircle'
      };
    }

    return { name: typeKey, color: '#424242', icon: 'mdi-folder', iconShape: 'squircle' };
  };

  return {
    resolveHexColor,
    getStatusStyle,
    getEntityStyle,
    getProjectTypeStyle
  };
}
